/**
 * Evidence conflict detection — Documents_On_Site Prompt 12.
 *
 * For every SED section that has at least one supporting doc (set by
 * Prompt 11 in `EvidenceSection.supportingDocIds`), run a Gemini
 * comparison call asking "do the provider's value and the document's
 * value disagree, and which is more authoritative for this kind of
 * evidence?". Persists the resulting EvidenceConflict[] on the SED so
 * the section editor + report quality panel can render flags without
 * re-running Gemini on every render.
 *
 * Pure-ish: takes the structured-JSON Gemini call as a dependency so
 * unit tests can pin a deterministic response. Per-section failures
 * are caught and the section is skipped — no global throw.
 *
 * False-conflict suppression: trivial unit / precision differences
 * (e.g. "0.123g" vs "123 mg/g") are filtered client-side before the
 * Gemini call so the AI sees only material disagreements. The
 * `looksTriviallyEqual` helper is exported for tests.
 */
import { Type, runGeminiJSON } from './geminiClient';
import { tieredModelFor } from '../config/modelTiering';
import type { SiteEvidenceDocument, EvidenceSection } from '../types/siteEvidence';
import type { SiteDocument } from '../types/siteRag';
import type { ExtractedClaim } from '../types/siteContext';
import type { ReportDerivedClaim } from '../types/reportClaim';
import { docValueIsImplausible, lookupFieldType, CONFLICT_PIPELINE_VERSION, type FieldType } from '../lib/conflictPrefilter';
// #sed-owner-path — a SED's object key is OWNER-scoped and is built in ONE
// place; see the module header for the shared-site fork this prevents.
import { resolveSedInPlaceSavePath } from '../lib/sed/sedStoragePath';

export { CONFLICT_PIPELINE_VERSION };

export type ConflictSeverity = 'low' | 'medium' | 'high';
export type ConflictResolution = 'prefer-doc' | 'prefer-provider' | 'prefer-report' | 'manual-review';

export interface EvidenceConflict {
  sectionId: string;
  field: string;
  providerValue: unknown;
  providerSource: string;
  docValue: unknown;
  docId: string;
  docName: string;
  severity: ConflictSeverity;
  explanation: string;
  suggestedResolution: ConflictResolution;
  /** When the user picks a resolution, persisted alongside the conflict. */
  userResolution?: ConflictResolution;
  resolvedAt?: string;
  resolvedBy?: string;
  /**
   * ICR-1 — true when the conflict was machine-resolved by the
   * type/sanity prefilter (doc value implausible for the declared
   * field type). The detector still persists the row for audit /
   * telemetry but the panel filters auto-resolved rows out of the
   * main list by default; they appear in a `(N auto-resolved)`
   * disclosure. Absent ⇒ surface as a normal user-facing conflict.
   */
  autoResolved?: boolean;
  /**
   * ICR-1 — human-readable reason the prefilter rejected the doc
   * value (e.g. `'apostrophe-prefix fragment'`, `'not a 4-digit
   * year (got "from NTP.")'`). Surfaced in the disclosure so the
   * user can audit the prefilter's decisions.
   */
  autoResolvedReason?: string;
  /**
   * ICR-1 — convenience marker the panel reads to decide whether
   * the row should render a one-click `[✓ Confirm …]` affordance.
   * Mirrors the Gemini comparison's `authoritative` field for
   * comparison-driven conflicts, and is stamped `'provider'` for
   * prefilter auto-resolutions.
   *
   * RDX-6 widens the union with `'report'` for 3-axis conflicts where
   * the prior generated report's finding is the authoritative source.
   */
  authoritative?: 'provider' | 'document' | 'report' | 'manual-review';
  /**
   * ICR-4 — when the conflict is between a user-authored claim and
   * the provider value (instead of doc vs provider), these three
   * fields carry the authored side. `docValue` is left undefined
   * for the authored-vs-provider variant. Optional / back-compat:
   * absent ⇒ legacy doc-vs-provider pair.
   */
  authoredValue?: unknown;
  authoredClaimId?: string;
  authoredBy?: string;
  /**
   * RDX-6 — when the conflict involves a prior-report-derived value
   * (third axis alongside provider + document), these three fields
   * carry the report side. Absent ⇒ legacy two-axis conflict (no
   * regression for ICR's existing prefilter / `autoResolved` flow,
   * which only inspects provider + doc).
   *
   * `reportValue` is the typed value extracted from the prior report
   * (e.g. `145` for `grid.estimatedCapacityMW`).
   * `reportId` lets the UI deep-link "Feasibility v3 - Final
   * Recommendation" to the originating report.
   * `reportSectionId` is the originating section id within the report.
   */
  reportValue?: unknown;
  reportId?: string;
  reportSectionId?: string;
  /**
   * ICR-FOLLOWUP-3 — monotonic version of the conflict-detection
   * pipeline that produced this row. Bumped in
   * `conflictPrefilter.CONFLICT_PIPELINE_VERSION` whenever the
   * classifier / stopword list / field-type registry changes in a way
   * that would change auto-resolution. The bootstrap merger drops
   * prior cached conflicts whose version is missing or `<` the current
   * value so the detector re-runs against the upgraded classifier.
   * Absent ⇒ legacy pre-FOLLOWUP-3 row → treat as stale.
   */
  pipelineVersion?: number;
}

/* ── False-conflict suppression ─────────────────────────────────── */

/**
 * Numeric trivial-equality band. `abs` = absolute slack; `rel` =
 * relative (fraction-of-magnitude) slack. Two numbers are "the same
 * enough" when their difference is ≤ `abs` OR ≤ `rel × max(|a|,|b|)`.
 */
export interface TrivialThreshold {
  abs: number;
  rel: number;
}

/**
 * SP4 S1 — the DEFAULT numeric trivial-equality band. Identical to the
 * historic hard-coded values (0.05 absolute / 2% relative), so any call
 * to `looksTriviallyEqual(a, b)` WITHOUT a `fieldType` reproduces the
 * pre-S1 behaviour byte-for-byte. This is the additive guarantee: no
 * existing conflict set moves unless a field is explicitly given a wider
 * band below.
 */
export const DEFAULT_TRIVIAL_THRESHOLD: Readonly<TrivialThreshold> = { abs: 0.05, rel: 0.02 };

/**
 * SP4 S1 — per-field-type trivial-equality bands. Looked up ONLY when a
 * caller passes a `fieldType`; absent types fall back to
 * `DEFAULT_TRIVIAL_THRESHOLD`. Entries here may ONLY WIDEN the band
 * (never tighten it): BOTH `abs` and `rel` MUST be ≥ the corresponding
 * default (asserted at module load below). Widening can suppress more
 * near-identical pairs but can never START flagging a pair that the
 * default band already considered trivially equal — that is the S1
 * additive guarantee.
 *
 * Why these bands:
 *   - `mw` / `kw`: capacity figures routinely differ by sub-MW rounding
 *     between a provider snapshot and a vendor doc. The default band
 *     already suppresses small relative diffs (≤2%); the absolute floor
 *     is widened to 0.5 so a tiny absolute gap on a SMALL value (e.g. a
 *     1 vs 1.4 MW auxiliary load — 40% rel, but 0.4 abs) is treated as
 *     rounding noise rather than a material disagreement.
 *   - `percent`: capacity factors / humidity / risk percentages are
 *     reported to ~0.1 precision; a 0.2-absolute floor suppresses
 *     rounding-only diffs on small percentages.
 *
 * The relative slack is kept at the default (0.02) for every entry — we
 * only ever RAISE the absolute floor, which is the strictly-widening
 * direction. NOTE: numeric / integer / date / string / enum / year /
 * us-state / boolean intentionally have NO override — they keep the
 * default band, so their conflict detection is unchanged.
 */
const TRIVIAL_THRESHOLD_BY_TYPE: Partial<Record<FieldType, Readonly<TrivialThreshold>>> = {
  mw: { abs: 0.5, rel: 0.02 },
  kw: { abs: 0.5, rel: 0.02 },
  percent: { abs: 0.2, rel: 0.02 },
};

// SP4 S1 additive-guarantee invariant — every override must WIDEN (or
// equal) the default band on BOTH axes. A tightening entry would start
// flagging pairs the default suppressed, breaking the guarantee. Caught
// at module load (and in the unit test) so a future edit can't silently
// regress it.
for (const [type, band] of Object.entries(TRIVIAL_THRESHOLD_BY_TYPE)) {
  if (band.abs < DEFAULT_TRIVIAL_THRESHOLD.abs || band.rel < DEFAULT_TRIVIAL_THRESHOLD.rel) {
    throw new Error(
      `[evidenceConflict] TRIVIAL_THRESHOLD_BY_TYPE['${type}'] tightens the default band `
      + `(abs ${band.abs} / rel ${band.rel} vs default abs ${DEFAULT_TRIVIAL_THRESHOLD.abs} / `
      + `rel ${DEFAULT_TRIVIAL_THRESHOLD.rel}) — overrides may only widen.`,
    );
  }
}

/**
 * Resolve the numeric trivial-equality band for an optional field type.
 * Returns `DEFAULT_TRIVIAL_THRESHOLD` when no type is supplied or the
 * type has no override — preserving the historic behaviour.
 */
export function trivialThresholdForFieldType(fieldType?: FieldType): Readonly<TrivialThreshold> {
  if (!fieldType) return DEFAULT_TRIVIAL_THRESHOLD;
  return TRIVIAL_THRESHOLD_BY_TYPE[fieldType] ?? DEFAULT_TRIVIAL_THRESHOLD;
}

/**
 * Returns true when two values are "the same enough" that a Gemini
 * comparison would burn tokens on a non-issue: identical strings
 * (case-insensitive), numerically close (within the field's trivial
 * band), or one is a substring of the other.
 *
 * SP4 S1 — the optional `fieldType` selects a per-type numeric band
 * (`TRIVIAL_THRESHOLD_BY_TYPE`). Omitted ⇒ the historic default band
 * (0.05 absolute / 2% relative), so legacy callers are byte-identical.
 * The override bands only ever WIDEN, so passing a `fieldType` can only
 * suppress MORE near-identical pairs — it can never start flagging a
 * pair the default already deemed trivial.
 */
export function looksTriviallyEqual(a: unknown, b: unknown, fieldType?: FieldType): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a === 'number' && typeof b === 'number') {
    if (Number.isNaN(a) && Number.isNaN(b)) return true;
    if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
    const { abs, rel } = trivialThresholdForFieldType(fieldType);
    const diff = Math.abs(a - b);
    if (diff <= abs) return true;
    const denom = Math.max(Math.abs(a), Math.abs(b));
    if (denom > 0 && diff / denom <= rel) return true;
    return false;
  }
  if (typeof a === 'string' && typeof b === 'string') {
    const ai = a.trim().toLowerCase();
    const bi = b.trim().toLowerCase();
    if (ai === bi) return true;
    if (ai.length === 0 || bi.length === 0) return false;
    return ai.includes(bi) || bi.includes(ai);
  }
  return false;
}

/* ── Gemini comparison ───────────────────────────────────────────── */

interface ComparisonInput {
  sectionId: string;
  field: string;
  providerValue: unknown;
  providerSource: string;
  docValue: unknown;
  docName: string;
  runner?: typeof runGeminiJSON;
}

interface ComparisonResponse {
  inConflict: boolean;
  severity: ConflictSeverity;
  explanation: string;
  authoritative: 'provider' | 'document' | 'manual-review';
}

const COMPARISON_SCHEMA = {
  type: Type.OBJECT,
  properties: {
    inConflict: { type: Type.BOOLEAN },
    severity: { type: Type.STRING },
    explanation: { type: Type.STRING },
    authoritative: { type: Type.STRING },
  },
  required: ['inConflict', 'severity', 'explanation', 'authoritative'],
};

async function runComparison(input: ComparisonInput): Promise<ComparisonResponse | null> {
  const runner = input.runner ?? runGeminiJSON;
  const contents =
    `Two pieces of evidence about the same field disagree. Decide whether the disagreement is material, and which source is more authoritative for this kind of evidence.\n\n` +
    `SED SECTION: ${input.sectionId}\n` +
    `FIELD: ${input.field}\n` +
    `PROVIDER VALUE (${input.providerSource}): ${JSON.stringify(input.providerValue)}\n` +
    `DOCUMENT VALUE (uploaded by user — "${input.docName}"): ${JSON.stringify(input.docValue)}\n\n` +
    `Reply with JSON:\n` +
    `  inConflict   — true only if the values would lead to different conclusions in a feasibility report.\n` +
    `  severity     — "low" | "medium" | "high". Low = trivial unit/precision; medium = different categorisation; high = opposite go/no-go implications.\n` +
    `  explanation  — one sentence on why these values disagree (or don't).\n` +
    `  authoritative — "provider" (e.g. official FEMA / USGS dataset wins for regulatory data), "document" (site-specific certificate or signed letter overrides regional dataset), "manual-review" (cannot decide without more context).\n`;
  try {
    // Cost lever (strategy §4): per-(section×doc) scalar comparison is
    // judgment-light fan-out — Flash when tiering is on, Pro when off.
    return await runner<ComparisonResponse>({
      model: tieredModelFor('evidenceConflict'),
      contents,
      schema: COMPARISON_SCHEMA,
      timeoutMs: 30_000,
    });
  } catch (e) {
    console.warn(`[evidenceConflict] comparison failed for ${input.sectionId}.${input.field}:`, (e as Error)?.message);
    return null;
  }
}

/* ── Per-section field extraction ─────────────────────────────── */

/**
 * Pull a flat {field → value} map out of an evidence section. Skips
 * arrays and nested objects so the Gemini comparison stays scoped to
 * scalar values that actually correspond between provider + doc data.
 */
function flattenSection(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== 'object') return {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
    if (v == null) continue;
    if (typeof v === 'object') continue;          // skip nested
    out[k] = v;
  }
  return out;
}

/* ── Public entry point ──────────────────────────────────────── */

export interface DetectEvidenceConflictsOptions {
  /** Override for tests; defaults to runGeminiJSON. */
  runner?: typeof runGeminiJSON;
  /** Concurrency cap for Gemini comparison calls. Default 4. */
  concurrency?: number;
  /**
   * ICR-4 — user-authored claims to compare against provider values.
   * When supplied, the pair-builder adds a second pass that walks
   * authored claims whose `requirementId` maps onto a `'provider'`
   * provenance SED slot. Disagreements surface as standalone
   * `authoredValue`-bearing conflict rows. Absent ⇒ legacy
   * doc-vs-provider behaviour only.
   */
  authoredClaims?: readonly ExtractedClaim[];
  /**
   * ICR-4 — requirementId → SED-section map. Defaults to the platform's
   * `REQUIREMENT_TO_SECTION_MAP`; injectable for tests so the
   * authored-vs-provider pass can be exercised without a real
   * requirement registry.
   */
  requirementToSectionMap?: Record<string, ReadonlyArray<string>>;
  /**
   * RDX-6 — prior-report-derived claims. When supplied, the detector
   * runs a third pass that walks every CURRENT (non-superseded) claim
   * whose `sedFieldPath` matches a populated provider/doc field, and
   * surfaces a 3-axis conflict row when the report value materially
   * disagrees with the resolved SED value. Absent ⇒ legacy two-axis
   * behaviour preserved (ICR's prefilter + autoResolved untouched).
   *
   * The 3-axis pass runs in parallel to (not on top of) the existing
   * passes. It NEVER calls Gemini — the trigger is a structural
   * mismatch between report and provider, and the resolution
   * affordance is deterministic (the user picks `'prefer-report'` /
   * `'prefer-provider'` / `'manual-review'`).
   */
  priorReportClaims?: readonly ReportDerivedClaim[];
  /**
   * DAT-FOLLOWUP-2 — site-bound state prior. When supplied, the
   * detector runs an additional pass that walks every section
   * carrying a `state` field (regulatory, electricity) and surfaces a
   * HIGH-severity conflict when the section's resolved state
   * disagrees with the site prior.
   *
   * Why this is needed despite the existing doc-vs-provider pass:
   * the live-prod Redhead case had `regulatory` provider
   * 'unavailable', so the doc was the FIRST write — the legacy pass
   * had no provider value to compare against and the bad value was
   * silently committed.
   *
   * Two-letter USPS code (case-insensitive at lookup). `null` /
   * `undefined` ⇒ pass disabled (legacy behaviour). Set by callers
   * who can deterministically resolve the site state (typically
   * `inferSiteStateFromSed` — but for the conflict pass we want a
   * value that wasn't poisoned by the doc, so callers pass either an
   * `electricity.state`-derived value or a coordinate-derived value
   * directly).
   *
   * No Gemini call here — the trigger is a structural disagreement
   * between site-bound ground truth and the section's value. The
   * suggested resolution is deterministic: `'prefer-provider'` when
   * the section is provider-derived, otherwise `'manual-review'`.
   */
  siteState?: string | null;
}

/**
 * Walk every section that carries supportingDocIds. For each
 * field-shaped scalar, compare the provider value against the doc's
 * extracted value (when present). Trivial differences are suppressed
 * client-side. Returns the unresolved conflicts.
 *
 * To extract a doc's per-field "claim" we re-use the relevance
 * classifier's intuition — for each supporting doc, we look up the
 * field's name in the doc's textContent / summary and treat the
 * surrounding token as the doc's value. This is intentionally simple:
 * the Gemini comparison call adjudicates the actual disagreement.
 */
export async function detectEvidenceConflicts(
  sed: SiteEvidenceDocument,
  docs: SiteDocument[],
  options: DetectEvidenceConflictsOptions = {},
): Promise<EvidenceConflict[]> {
  const docsById = new Map(docs.map(d => [d.id, d]));
  const conflicts: EvidenceConflict[] = [];

  // Collect every (section, field, doc) triplet that warrants a comparison.
  type Pair = { sectionId: string; section: EvidenceSection<unknown>; field: string; providerValue: unknown; doc: SiteDocument; docValue: unknown };
  const pairs: Pair[] = [];

  const sectionIds = Object.keys(sed) as Array<keyof SiteEvidenceDocument>;
  for (const sid of sectionIds) {
    const section = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[sid as string];
    if (!section || !section.supportingDocIds || section.supportingDocIds.length === 0) continue;
    if (section.status === 'unavailable') continue;
    const flat = flattenSection(section.data);
    for (const docId of section.supportingDocIds) {
      const doc = docsById.get(docId);
      if (!doc) continue;
      // Heuristic doc-value lookup — case-insensitive substring search
      // for the field name in the doc's text. Returns the snippet that
      // follows the field name; the Gemini call extracts the actual claim.
      for (const [field, providerValue] of Object.entries(flat)) {
        const docValue = extractDocValueForField(doc, field);
        if (docValue == null) continue;
        // SP4 S1 — pass the declared field type so capacity/percent
        // fields use their (wider) trivial band; the type lookup was
        // already computed below for the prefilter, hoisted here so the
        // trivial-equality check is type-aware too. Unregistered fields
        // resolve to the default band ⇒ unchanged behaviour.
        const fieldType = lookupFieldType(sid as string, field);
        if (looksTriviallyEqual(providerValue, docValue, fieldType)) continue;

        // ICR-1 — type/sanity prefilter. If the doc value is OBVIOUSLY
        // not a plausible answer for the field's declared schema type
        // (apostrophe-prefix fragment, non-numeric "year", non-state
        // "state", etc.), auto-resolve in favour of the provider and
        // skip the Gemini call entirely. Persisted with `autoResolved`
        // so the panel filters them out of the user-facing list but
        // keeps them in a disclosure for telemetry / audit.
        const check = docValueIsImplausible(field, fieldType, docValue);
        if (check.implausible) {
          conflicts.push({
            sectionId: sid as string,
            field,
            providerValue,
            providerSource: section.source,
            docValue,
            docId: doc.id,
            docName: doc.fileName,
            severity: 'low',
            explanation: `Auto-resolved: doc value implausible (${check.reason}). Provider value retained.`,
            suggestedResolution: 'prefer-provider',
            authoritative: 'provider',
            userResolution: 'prefer-provider',
            autoResolved: true,
            autoResolvedReason: check.reason,
            resolvedBy: 'icr-prefilter',
            resolvedAt: new Date().toISOString(),
            pipelineVersion: CONFLICT_PIPELINE_VERSION,
          });
          continue; // skip the Gemini comparison
        }

        pairs.push({ sectionId: sid as string, section, field, providerValue, doc, docValue });
      }
    }
  }

  // Run Gemini comparisons with bounded concurrency.
  const concurrency = options.concurrency ?? 4;
  let cursor = 0;
  // SD3 — per-(section, doc) timeout. runGeminiJSON itself already
  // carries a 30s budget, but historically that has not been enough to
  // protect bootstrap from a wholly-hung call (the SDK can retry
  // internally past the soft budget). Wrap each pair in an explicit
  // withTimeout so any single hung section is independently time-boxed
  // and the rest of the batch keeps making progress. The timeout label
  // identifies which pair stalled in the console.
  const { withTimeout, isTimeoutError } = await import('../lib/withTimeout');
  async function worker() {
    while (cursor < pairs.length) {
      const idx = cursor++;
      const p = pairs[idx]!;
      const label = `conflict-detect:${p.sectionId}:${p.doc.id}`;
      let cmp: ComparisonResponse | null = null;
      try {
        cmp = await withTimeout(
          runComparison({
            sectionId: p.sectionId,
            field: p.field,
            providerValue: p.providerValue,
            providerSource: p.section.source,
            docValue: p.docValue,
            docName: p.doc.fileName,
            runner: options.runner,
          }),
          30_000,
          label,
        );
      } catch (e) {
        if (isTimeoutError(e)) {
          console.warn(`[evidenceConflict] timeout: section=${p.sectionId} doc=${p.doc.id}`);
          continue; // single pair stalled — drop it, keep batch alive
        }
        throw e;
      }
      if (!cmp || !cmp.inConflict) continue;
      const authoritative: 'provider' | 'document' | 'manual-review' =
        cmp.authoritative === 'document' || cmp.authoritative === 'provider'
          ? cmp.authoritative
          : 'manual-review';
      conflicts.push({
        sectionId: p.sectionId,
        field: p.field,
        providerValue: p.providerValue,
        providerSource: p.section.source,
        docValue: p.docValue,
        docId: p.doc.id,
        docName: p.doc.fileName,
        severity: ((['low', 'medium', 'high'] as ConflictSeverity[]).includes(cmp.severity)
          ? cmp.severity : 'medium'),
        explanation: cmp.explanation || 'Values differ; review manually.',
        suggestedResolution: authoritative === 'document'
          ? 'prefer-doc'
          : authoritative === 'provider'
            ? 'prefer-provider'
            : 'manual-review',
        // ICR-2 — convenience marker the panel reads to decide whether
        // to render a one-click [✓ Confirm …] affordance.
        authoritative,
        pipelineVersion: CONFLICT_PIPELINE_VERSION,
      });
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, pairs.length) }, () => worker()));

  // ICR-4 — authored-vs-provider pass. When the caller supplied
  // `authoredClaims`, walk every claim whose `requirementId` maps onto
  // a section with `provenance: 'provider'` (i.e. provider-derived
  // data) and surface disagreements. The doc-side fields are left
  // empty; the panel renders a three-axis row when `authoredValue` is
  // present.
  const authoredClaims = options.authoredClaims ?? [];
  if (authoredClaims.length > 0) {
    // Lazy-load the default registry so test callers can stub via
    // `options.requirementToSectionMap`. Static-import keeps the prod
    // path fast (no dynamic import on the hot path).
    const map = options.requirementToSectionMap
      ?? (await import('../config/requirementToSection')).REQUIREMENT_TO_SECTION_MAP;
    for (const sid of sectionIds) {
      const section = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[sid as string];
      if (!section) continue;
      if (section.status === 'unavailable') continue;
      if (section.provenance && section.provenance !== 'provider' && section.provenance !== 'provider+doc'
        && section.provenance !== 'report+provider') continue;
      const flat = flattenSection(section.data);
      for (const claim of authoredClaims) {
        if (!claim.requirementId) continue;
        const sections = map[claim.requirementId];
        if (!sections || !sections.includes(sid as string)) continue;
        const sf = claim.structuredFields;
        if (!sf) continue;
        for (const [field, providerValue] of Object.entries(flat)) {
          const authoredValue = sf[field];
          if (authoredValue == null) continue;
          // SP4 S1 — type-aware trivial band (default for unregistered).
          if (looksTriviallyEqual(providerValue, authoredValue, lookupFieldType(sid as string, field))) continue;
          // The authored side IS the user's intent — no plausibility
          // prefilter here. A real disagreement is exactly what we want
          // to surface so the user can adjudicate.
          conflicts.push({
            sectionId: sid as string,
            field,
            providerValue,
            providerSource: section.source,
            // Doc-side fields left empty — UI keys on `authoredValue`
            // presence to render the three-axis variant.
            docValue: undefined,
            docId: '',
            docName: '',
            severity: 'medium',
            explanation: `Authored value ${JSON.stringify(authoredValue)} disagrees with provider value ${JSON.stringify(providerValue)}.`,
            suggestedResolution: 'manual-review',
            authoritative: 'manual-review',
            authoredValue,
            authoredClaimId: claim.id,
            authoredBy: claim.writtenBy ?? claim.lastWrittenBy?.email ?? claim.lastWrittenBy?.uid,
            pipelineVersion: CONFLICT_PIPELINE_VERSION,
          });
        }
      }
    }
  }

  // RDX-6 — Third pass: prior-report-derived claims vs provider (and
  // optionally a doc value where one was already extracted in the
  // doc-vs-provider pass). When the report's finding materially
  // disagrees with the resolved SED slot value, surface a 3-axis row
  // the user can adjudicate.
  //
  // Constitutional invariants:
  //   - ICR's prefilter / `autoResolved` flow is UNTOUCHED. The
  //     prefilter only inspects (provider, doc); the report axis is
  //     evaluated separately. Existing two-axis conflicts pass through
  //     unchanged.
  //   - No Gemini call here. Report-derived claims already carry the
  //     LLM-blessed value from their originating report run; running
  //     another LLM call would just amplify drift. The user picks the
  //     authoritative axis deterministically.
  //   - The pass is purely additive — every conflict it pushes carries
  //     `reportValue` (defined) and `reportId` (non-empty). Two-axis
  //     consumers branch on `reportValue !== undefined` to render the
  //     3-column variant.
  const priorReportClaims = options.priorReportClaims ?? [];
  if (priorReportClaims.length > 0) {
    // Index current (non-superseded) claims by sedFieldPath for O(1)
    // lookup per section/field.
    const claimsByPath = new Map<string, ReportDerivedClaim>();
    for (const c of priorReportClaims) {
      if (c.supersededBy != null) continue;
      const prior = claimsByPath.get(c.sedFieldPath);
      if (!prior || c.createdAt > prior.createdAt) {
        claimsByPath.set(c.sedFieldPath, c);
      }
    }

    for (const sid of sectionIds) {
      const section = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[sid as string];
      if (!section) continue;
      if (section.status === 'unavailable') continue;
      // Skip slots already adopted by the report-derived tier — the
      // resolver has already chosen the report value, so there's no
      // disagreement to surface. The check uses status / provenance
      // narrow so a slot stamped `verified-from-report` short-circuits.
      if (section.status === 'verified-from-report') continue;
      if (section.provenance === 'report-derived'
        || section.provenance === 'report-overrides-provider') continue;
      // Don't double-flag a slot whose user-authored or research tier
      // already won — those rows would already be surfaced by ICR-4 /
      // the resolver's lineage.
      if (section.status === 'verified-from-context') continue;
      if (section.status === 'verified-from-research') continue;

      const flat = flattenSection(section.data);
      for (const [field, providerValue] of Object.entries(flat)) {
        const path = `${sid}.${field}`;
        const claim = claimsByPath.get(path);
        if (!claim) continue;
        // SP4 S1 — type-aware trivial band (default for unregistered).
        if (looksTriviallyEqual(providerValue, claim.value, lookupFieldType(sid as string, field))) continue;

        // SED D3.6 — auto-close. The report→SED resolver reconciled this
        // claim: if it was APPLIED (the report value won and is now the
        // resolved slot value) OR its value now triviallyEquals the
        // resolved slot, the disagreement is settled. Push the row stamped
        // `userResolution: 'prefer-report'` so it's recorded for audit but
        // counted as RESOLVED (countUnresolvedConflicts filters it out) —
        // a confirmed-back claim stops re-raising the same conflict on
        // every rebuild. Skipping the push entirely would lose the audit
        // trail; stamping it resolved is the honest, non-silent close.
        const resolvedBack =
          claim.resolution?.state === 'applied' ||
          looksTriviallyEqual(claim.value, providerValue, lookupFieldType(sid as string, field));
        if (resolvedBack) {
          conflicts.push({
            sectionId: sid as string,
            field,
            providerValue,
            providerSource: section.source,
            docValue: undefined,
            docId: '',
            docName: '',
            severity: 'low',
            explanation: `Prior ${claim.reportType} v${claim.reportVersion} value reconciled into the SED (resolution: ${claim.resolution?.state ?? 'value-matched'}); no live disagreement.`,
            suggestedResolution: 'prefer-report',
            authoritative: 'report',
            userResolution: 'prefer-report',
            resolvedAt: new Date(claim.resolution?.resolvedAt ?? Date.now()).toISOString(),
            resolvedBy: 'auto:report-reconciliation',
            reportValue: claim.value,
            reportId: claim.reportId,
            reportSectionId: claim.sourceSectionId,
            pipelineVersion: CONFLICT_PIPELINE_VERSION,
          });
          continue;
        }

        // The conflict is real — push a 3-axis row. `authoritative` is
        // left as `'manual-review'` by default; the future Gemini-mediated
        // reconciliation (RDX-FOLLOWUP-CONFLICT-AUTO-RESOLUTION) can
        // promote this to `'report'` automatically.
        const sev: ConflictSeverity = typeof claim.value === 'number'
          && typeof providerValue === 'number'
          && Math.abs((claim.value as number) - (providerValue as number)) / Math.max(Math.abs(claim.value as number), Math.abs(providerValue as number)) > 0.25
            ? 'high'
            : 'medium';
        conflicts.push({
          sectionId: sid as string,
          field,
          providerValue,
          providerSource: section.source,
          // Doc fields empty — UI keys on `reportValue` to render the
          // 3-axis variant. If a doc-vs-provider pass also flagged this
          // (section, field, docId), the panel groups by (section,
          // field) and renders both rows.
          docValue: undefined,
          docId: '',
          docName: '',
          severity: sev,
          explanation: `Prior ${claim.reportType} v${claim.reportVersion} concluded ${JSON.stringify(claim.value)}; provider says ${JSON.stringify(providerValue)}.`,
          suggestedResolution: 'manual-review',
          authoritative: 'manual-review',
          reportValue: claim.value,
          reportId: claim.reportId,
          reportSectionId: claim.sourceSectionId,
          pipelineVersion: CONFLICT_PIPELINE_VERSION,
        });
      }
    }
  }

  // DAT-FOLLOWUP-2 — Fourth pass: site-state ground truth vs the SED's
  // resolved `state` fields. Closes the gap where the LIVE-PROD Redhead
  // bug surfaced — `regulatory` provider 'unavailable' meant the doc's
  // "Tennessee" was the FIRST write, so the legacy doc-vs-provider pass
  // had no provider value to compare against and committed the bad
  // value silently. Now we ALSO compare every section's `state` field
  // against an out-of-band site-state prior; disagreements fire a
  // HIGH-severity row even when the value was the only write.
  //
  // Constitutional invariants:
  //   - ICR's prefilter / `autoResolved` flow is UNTOUCHED — that
  //     prefilter only inspects doc-extracted scalar values; this pass
  //     compares already-merged section data against a site prior, not
  //     doc-extracted tokens.
  //   - No Gemini call — the trigger is a structural disagreement
  //     between site-bound ground truth and the section's value.
  //   - The pass is purely additive — every conflict it pushes carries
  //     `field === 'state'` AND `providerValue !== siteState`. Two-axis
  //     consumers see a normal-shaped conflict row; the
  //     `crossStateRejection` audit flag is conveyed via the
  //     explanation text + the `authoritative: 'provider'` marker.
  //   - The pass NEVER fires when the section's value normalises to
  //     the SAME state code as `siteState` — same-state values are
  //     happy-path noise the live-prod Redhead bug doesn't exhibit.
  //   - The pass DOES still fire when the section is provider-derived
  //     (`provenance === 'provider'`) but disagrees with `siteState` —
  //     this catches the inverse bug where a stale curated provider
  //     entry contradicts a geocoded boundary.
  const siteStatePrior = (() => {
    const raw = options.siteState;
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    // Use the inferSiteState normaliser so a caller passing 'louisiana'
    // or 'Louisiana' or 'la' all resolve to 'LA'.
    // Lazy-import to avoid pulling regulatoryDb into the
    // evidenceConflict module bundle at top level.
    return trimmed.toUpperCase();
  })();
  if (siteStatePrior) {
    // Lazy-import the normaliser only when the pass is active.
    const { normaliseStateToCode } = await import('../lib/inferSiteState');
    const priorCode = normaliseStateToCode(siteStatePrior);
    if (priorCode) {
      // Sections whose schema carries a `state` field. Mirrors
      // SCHEMA_HAS_STATE_FIELD in `extractFromDocument.ts`.
      const STATE_FIELD_SECTIONS = ['regulatory', 'electricity'] as const;
      for (const sid of STATE_FIELD_SECTIONS) {
        const section = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[sid];
        if (!section) continue;
        if (section.status === 'unavailable') continue;
        const data = section.data as { state?: unknown } | null;
        const sectionState = data?.state;
        if (typeof sectionState !== 'string' || sectionState.trim().length === 0) continue;
        const sectionStateCode = normaliseStateToCode(sectionState);
        if (!sectionStateCode) continue;             // unrecognised — skip (don't blame)
        if (sectionStateCode === priorCode) continue; // happy path
        // The section's state disagrees with the site prior. Push a
        // HIGH-severity row. Resolution defaults to 'prefer-provider'
        // when the section is provider-derived; 'manual-review'
        // otherwise (doc / report / authored — the user must adjudicate).
        const isProviderTier = section.provenance === 'provider'
          || section.provenance === 'provider+doc';
        const docCtx = (() => {
          // When the section carries supportingDocIds, attribute the
          // mismatch to the first supporting doc so the panel can deep-
          // link to it. Otherwise leave empty.
          const dids = section.supportingDocIds ?? [];
          if (dids.length === 0) return { docId: '', docName: '' };
          const did = dids[0];
          const doc = did != null ? docsById.get(did) : undefined;
          return { docId: did ?? '', docName: doc?.fileName ?? '' };
        })();
        conflicts.push({
          sectionId: sid,
          field: 'state',
          providerValue: sectionState,
          providerSource: section.source,
          docValue: undefined,
          ...docCtx,
          severity: 'high',
          explanation:
            `Site-bound state prior is ${priorCode}; ${sid}.state resolves to ` +
            `${sectionStateCode} (${JSON.stringify(sectionState)}). Cross-state ` +
            `attribution risk — verify the value applies to THIS site, not a ` +
            `referenced jurisdiction (gas pipeline, parent HQ, precedent).`,
          suggestedResolution: isProviderTier ? 'manual-review' : 'prefer-provider',
          authoritative: isProviderTier ? 'manual-review' : 'provider',
        });
      }
    }
  }

  return conflicts;
}

/**
 * Extract a doc's "claim" for a field name by scanning its text +
 * summary. Returns the value-shaped token that follows the field name
 * (case-insensitive), or null when no mention is found.
 *
 * Tiny + heuristic on purpose — the Gemini comparison call decides
 * whether the surfaced snippet is a real disagreement or noise.
 * Exported for tests.
 */
export function extractDocValueForField(doc: SiteDocument, field: string): unknown {
  const haystack = `${doc.summary ?? ''}\n${doc.textContent ?? ''}`;
  if (!haystack) return null;
  // Convert camelCase to space-delimited so "floodZone" matches "Flood Zone"
  // in human-written documents.
  const pretty = field.replace(/([A-Z])/g, ' $1').trim();
  const patterns = [field, pretty, pretty.toLowerCase()];
  for (const pat of patterns) {
    const re = new RegExp(`${pat}\\s*[:=\\-—]?\\s*([\\w\\d\\.\\-\\/%]+(?:\\s+[\\w\\d\\.\\-\\/%]+)?)`, 'i');
    const m = re.exec(haystack);
    if (!m) continue;
    return m[1];
  }
  return null;
}

/* ── Persistence on the SED ──────────────────────────────────── */

/**
 * Stamp the conflict list onto a SED's metadata. Stored alongside the
 * regular fields so a single GCS save persists everything. Unresolved
 * conflicts are kept verbatim; resolved entries carry the picker
 * value + a timestamp so the audit trail survives across versions.
 */
export function applyConflictsToSed(
  sed: SiteEvidenceDocument,
  conflicts: EvidenceConflict[],
): SiteEvidenceDocument {
  // Stash on a typed augmentation field. Cast through unknown to keep
  // the type extension narrow; `sed.evidenceConflicts` is declared on
  // SiteEvidenceDocument in src/types/siteEvidence.ts (Prompt 12-A).
  const next = { ...sed } as SiteEvidenceDocument & {
    evidenceConflicts?: EvidenceConflict[];
    conflictsPipelineVersion?: number;
  };
  next.evidenceConflicts = conflicts;
  // ICR-FU-3 — stamp the pipeline version so the bootstrap merge step
  // can detect when prior-cached conflicts came from a stale prefilter
  // and discard them instead of merging with garbage rows.
  next.conflictsPipelineVersion = CONFLICT_PIPELINE_VERSION;
  return next;
}

/* ── Live unresolved-conflict counts (CB1) ───────────────────────── */

/**
 * Shape of the count payload stamped on `SiteRagReport.unresolvedConflicts`
 * at generation time and returned by the live derivation at banner-render
 * time. Pinned here (not on the report type) so both writers share one
 * definition and the live/stamped counts stay structurally identical.
 */
export interface ConflictCounts {
  total: number;
  bySeverity: { low: number; medium: number; high: number };
  items: Array<{
    sectionId: string;
    field: string;
    severity: ConflictSeverity;
    explanation: string;
  }>;
}

/**
 * Shared severity-grouping helper. Returns the *unresolved* slice of a
 * conflict list bucketed by severity, plus the first 5 items for the
 * Quality widget. Called from BOTH the gen-time stamping site in
 * `siteRagService` AND `computeLiveUnresolvedConflicts` below so the
 * two counts can only disagree because the underlying list differs
 * (i.e. the user resolved something), never because of grouping drift.
 */
export function countUnresolvedConflicts(
  conflicts: readonly EvidenceConflict[] | null | undefined,
): ConflictCounts {
  const empty: ConflictCounts = { total: 0, bySeverity: { low: 0, medium: 0, high: 0 }, items: [] };
  if (!conflicts || conflicts.length === 0) return empty;
  // ICR-1 — prefilter auto-resolutions are already stamped
  // `userResolution: 'prefer-provider'` so the existing predicate
  // suppresses them; the explicit `!c.autoResolved` guard documents
  // the invariant for future readers.
  const unresolved = conflicts.filter(c => !c.userResolution && !c.autoResolved);
  if (unresolved.length === 0) return empty;
  const bySeverity = { low: 0, medium: 0, high: 0 };
  for (const c of unresolved) bySeverity[c.severity] = (bySeverity[c.severity] ?? 0) + 1;
  return {
    total: unresolved.length,
    bySeverity,
    items: unresolved.slice(0, 5).map(c => ({
      sectionId: c.sectionId,
      field: c.field,
      severity: c.severity,
      explanation: c.explanation,
    })),
  };
}

/**
 * Live derivation of unresolved-conflict counts from a current SED.
 *
 * The report's `unresolvedConflicts` is a gen-time snapshot — stamped
 * once during report assembly and never mutated again (reproducibility
 * invariant: opening a v3 report a year from now must show the v3-era
 * conflict state). When the user resolves a conflict in the SED
 * editor, the resolution writes to the site's SED but does NOT
 * touch the report snapshot, so the report-top banner would otherwise
 * shame the user about already-fixed conflicts forever.
 *
 * Callers (`pickReportAlert` — the report header status chip's conflicts
 * state — via the parent's live store subscription) pass the *current*
 * site SED so the state re-derives on every SED mutation. The stamped
 * snapshot stays on the report for the audit
 * trail in the Data Quality Notes appendix.
 *
 * Returns the empty payload when no SED is available — the banner
 * then falls back to the stamped count and behaves as it did before
 * CB2 (cf. legacy reports on deleted sites).
 */
export function computeLiveUnresolvedConflicts(
  sed: SiteEvidenceDocument | null | undefined,
): ConflictCounts {
  if (!sed) return { total: 0, bySeverity: { low: 0, medium: 0, high: 0 }, items: [] };
  const all = (sed as SiteEvidenceDocument & { evidenceConflicts?: EvidenceConflict[] }).evidenceConflicts;
  return countUnresolvedConflicts(all);
}

/**
 * Apply a single resolution to a conflict list, returning a new
 * array with the matched entry stamped. Pure — caller is responsible
 * for persisting the result.
 */
export function applyConflictResolution(
  conflicts: EvidenceConflict[],
  match: { sectionId: string; field: string; docId: string },
  resolution: ConflictResolution,
  resolvedBy: string,
): EvidenceConflict[] {
  return conflicts.map(c =>
    c.sectionId === match.sectionId && c.field === match.field && c.docId === match.docId
      ? { ...c, userResolution: resolution, resolvedAt: new Date().toISOString(), resolvedBy }
      : c,
  );
}

/**
 * Persist a conflict-update by re-saving the SED to GCS. Lives here
 * (rather than in sedBootstrap) so the section editor can reach it
 * without pulling in the bootstrap pipeline. Bumps the SED's `version` so
 * downstream readers (DocumentLibrary, the Quality panel) know the
 * SED has changed.
 *
 * `canonicalStoragePath` is the owning site's `sedStoragePath`, and it is what
 * makes the write land on the object the read side loads. Built from `uid`
 * instead, a resolution on a company-shared (or legacy flat-path) site writes
 * into the CALLER's namespace while the canonical object keeps the unresolved
 * conflicts — the "I resolved these six and they came back" report. Callers
 * that can reach the site doc MUST pass it; the returned `storagePath` is the
 * object actually written.
 */
const PENDING_CONFLICT_SAVE_ENVELOPES = new Map<
  string,
  { collectedAt: string; operationId: string }
>();

export async function persistSedConflicts(
  uid: string,
  siteId: string,
  sed: SiteEvidenceDocument,
  conflicts: EvidenceConflict[],
  canonicalStoragePath?: string | null,
  options?: { operationId?: string },
): Promise<{ ok: boolean; storagePath: string; version: number }> {
  const path = resolveSedInPlaceSavePath(uid, siteId, sed.id, canonicalStoragePath);
  const envelopeKey = JSON.stringify([
    uid,
    siteId,
    sed.id,
    sed.version ?? 1,
    canonicalStoragePath ?? path,
    conflicts,
  ]);
  const existingEnv = !options?.operationId
    ? PENDING_CONFLICT_SAVE_ENVELOPES.get(envelopeKey)
    : undefined;
  const collectedAt = existingEnv?.collectedAt ?? new Date().toISOString();
  const operationId =
    options?.operationId?.trim() ||
    existingEnv?.operationId ||
    `stage-conflicts-${siteId}-${sed.id}-v${(sed.version ?? 1) + 1}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`.replace(
      /[^A-Za-z0-9._:-]/g,
      '-',
    );
  if (!options?.operationId) {
    PENDING_CONFLICT_SAVE_ENVELOPES.set(envelopeKey, { collectedAt, operationId });
  }
  const next = applyConflictsToSed(
    { ...sed, version: (sed.version ?? 1) + 1, collectedAt },
    conflicts,
  );
  const { stageAndPublishSedDocument } = await import('./sedLoader');
  const published = await stageAndPublishSedDocument({
    sed: next,
    storagePath: path,
    siteId,
    ownerUid: uid,
    operationId,
  });
  if (
    published.ok ||
    (published.status !== undefined && published.status >= 400 && published.status < 500) ||
    published.error?.includes('refused') ||
    published.error?.includes('did not commit')
  ) {
    PENDING_CONFLICT_SAVE_ENVELOPES.delete(envelopeKey);
  }
  return {
    ok: published.ok,
    storagePath: published.ok ? published.storagePath : path,
    version: published.ok ? published.version : next.version,
  };
}
